import { Fragment, useState } from "react";
import { Table, Collapse, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { PaginationControls } from "../../../../../hooks/customHook/paginationHook/usePagination";
import { formatToNumber } from "../../../../../utils/numberFormatter";

const ProductDeficitAccordion = ({ pagination }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const { data, loading, totalPages } = pagination;

  const toggleRow = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" size="sm" /> Getting
        Replenishment Details...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted py-4 border rounded bg-light">
        <em>No replenishment history found for this period.</em>
      </div>
    );
  }

  if (data.length > 0 && data[0].deficitStock === undefined) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" size="sm" /> Loading
        Replenishment Format...
      </div>
    );
  }

  return (
    <>
      <Table
        bordered
        responsive
        hover
        className="text-nowrap align-middle text-center mb-0"
        style={{ tableLayout: "fixed", minWidth: "900px" }}
      >
        <thead className="table-light">
          <tr>
            <th rowSpan="2" style={{ width: "25%" }} className="align-middle">
              Transaction ID
            </th>
            <th rowSpan="2" style={{ width: "15%" }} className="align-middle">
              Deficit Stock
            </th>
            <th colSpan="2" style={{ width: "35%" }}>
              Replenishment
            </th>
            <th rowSpan="2" style={{ width: "20%" }} className="align-middle">
              Overall Remaining Deficit
            </th>
            <th rowSpan="2" style={{ width: "5%" }} className="align-middle">
              {/* Toggle Icon */}
            </th>
          </tr>
          <tr>
            <th>Transactions</th>
            <th>Total Stock Used</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const isExpanded = expandedRows.includes(row.id);

            return (
              <Fragment key={row.id}>
                {/* Parent Summary Row */}
                <tr
                  onClick={() => toggleRow(row.id)}
                  style={{ cursor: "pointer" }}
                  className={isExpanded ? "table-active" : ""}
                  title="Click to view details"
                >
                  <td className="fw-semibold text-truncate">
                    {row.transactionId || "N/A"}
                  </td>
                  <td className="text-danger fw-bold">
                    {formatToNumber(row.deficitStock)}
                  </td>
                  <td>{formatToNumber(row.replenishmentCount)}</td>
                  <td className="text-success fw-bold">
                    {formatToNumber(row.totalStockUsed)}
                  </td>
                  <td
                    className={`fw-bold ${
                      formatToNumber(row.overallRemaining) < 0
                        ? "text-danger"
                        : "text-success"
                    }`}
                  >
                    {formatToNumber(row.overallRemaining)}
                  </td>
                  <td className="text-muted">
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronUp : faChevronDown}
                    />
                  </td>
                </tr>

                {/* Child Details Row (Collapsible) */}
                <tr>
                  <td colSpan={6} className="p-0 border-0">
                    <Collapse in={isExpanded} unmountOnExit>
                      <div className="bg-light p-3 border-bottom shadow-inner">
                        <p className="fw-bold text-start mb-2 text-primary">
                          <i className="fa-solid fa-list-ul me-2"></i>
                          Replenishment Breakdown for {row.transactionId}
                        </p>

                        {row.details && row.details.length > 0 ? (
                          <Table
                            size="sm"
                            bordered
                            hover
                            className="text-nowrap align-middle text-center mb-0 bg-white"
                          >
                            <thead className="table-secondary">
                              <tr>
                                <th
                                  rowSpan="2"
                                  className="align-middle"
                                  style={{ width: "25%" }}
                                >
                                  Stock Needed
                                </th>
                                <th colSpan="2" style={{ width: "50%" }}>
                                  Replenishment Source
                                </th>
                                <th
                                  rowSpan="2"
                                  className="align-middle"
                                  style={{ width: "25%" }}
                                >
                                  Remaining Deficit
                                </th>
                              </tr>
                              <tr>
                                <th>Transaction ID</th>
                                <th>Stock Used</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.details.map((detail) => (
                                <tr key={detail.id}>
                                  <td className="text-danger fw-bold">
                                    {formatToNumber(detail.stockNeeded)}
                                  </td>
                                  <td className="text-truncate">
                                    {detail.sourceTransactionId || "N/A"}
                                  </td>
                                  <td className="text-success fw-bold">
                                    {formatToNumber(detail.stockUsed)}
                                  </td>
                                  <td
                                    className={`fw-bold ${
                                      detail.remainingDeficit < 0
                                        ? "text-danger"
                                        : "text-success"
                                    }`}
                                  >
                                    {formatToNumber(detail.remainingDeficit)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="text-muted text-start ps-4 py-2">
                            <em>
                              No replenishment transactions exist for this
                              deficit.
                            </em>
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </Table>

      {/* Pagination Container */}
      {totalPages > 0 && <PaginationControls {...pagination} />}
    </>
  );
};

export { ProductDeficitAccordion };
